import React, { useMemo } from 'react';
import { useGameStore } from '../stores/gameStore';
import {
  formatNumber,
  getUnitsForTier,
  nextUnitCost,
  bulkUnitCost,
  maxAffordable,
  getTierDef,
} from '@annopolis/shared';
import type { UnitDef } from '@annopolis/shared';

const CHAIN_COLORS: Record<string, string> = {
  production: '#4af0c4',
  creation: '#a78bfa',
  knowledge: '#818cf8',
  energy: '#fbbf24',
  warfare: '#f87171',
};

const CHAIN_LABELS: Record<string, string> = {
  production: 'Production',
  creation: 'Creation',
  knowledge: 'Knowledge',
  energy: 'Energy',
  warfare: 'Warfare',
};

export function UnitPanel() {
  const { save, rates, selectedBuyQty, setSelectedBuyQty, buyUnit } = useGameStore();
  const units = useMemo(() => getUnitsForTier(save.tier), [save.tier]);
  const tierDef = getTierDef(save.tier);
  const primaryLabel = tierDef?.resources.primary ?? 'Matter';

  // Group by chain
  const grouped = useMemo(() => {
    const g: Record<string, UnitDef[]> = {};
    for (const u of units) {
      if (!g[u.chain]) g[u.chain] = [];
      g[u.chain].push(u);
    }
    return g;
  }, [units]);

  return (
    <div style={styles.panel}>
      {/* Buy quantity selector */}
      <div style={styles.qtyRow}>
        <span style={styles.qtyLabel}>Buy:</span>
        {([1, 10, 100, 'max'] as const).map((q) => (
          <button
            key={q}
            style={{ ...styles.qtyBtn, ...(selectedBuyQty === q ? styles.qtyBtnActive : {}) }}
            onClick={() => setSelectedBuyQty(q)}
          >
            {q === 'max' ? 'Max' : `×${q}`}
          </button>
        ))}
      </div>

      {/* Units by chain */}
      <div style={styles.scroll}>
        {(Object.keys(grouped) as Array<keyof typeof grouped>).map((chain) => (
          <div key={chain} style={styles.chainSection}>
            <div style={{ ...styles.chainHeader, color: CHAIN_COLORS[chain] }}>
              {CHAIN_LABELS[chain]}
            </div>
            {grouped[chain].map((def) => (
              <UnitRow
                key={def.id}
                def={def}
                owned={save.units[def.id] ?? 0}
                resources={save.resources.primary}
                tech={save.tech}
                selectedQty={selectedBuyQty}
                primaryLabel={primaryLabel}
                onBuy={(qty) => buyUnit(def.id, qty)}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function UnitRow({
  def,
  owned,
  resources,
  tech,
  selectedQty,
  primaryLabel,
  onBuy,
}: {
  def: UnitDef;
  owned: number;
  resources: number;
  tech: Record<string, number>;
  selectedQty: 1 | 10 | 100 | 'max';
  primaryLabel: string;
  onBuy: (qty: number) => void;
}) {
  const qty = selectedQty === 'max'
    ? maxAffordable(def, owned, resources, tech)
    : selectedQty;

  const cost = qty <= 0 ? nextUnitCost(def, owned, tech)
    : qty === 1
      ? nextUnitCost(def, owned, tech)
      : bulkUnitCost(def, owned, qty, tech);

  const canAfford = resources >= cost && qty > 0;
  const roleIcon = def.role === 'producer' ? '⬡' : def.role === 'builder' ? '⬢' : '◈';
  const roleColor = def.role === 'producer' ? '#4af0c4' : def.role === 'builder' ? '#818cf8' : '#fbbf24';

  return (
    <div style={{ ...styles.unitRow, opacity: owned === 0 && !canAfford ? 0.45 : 1 }}>
      <div style={styles.unitLeft}>
        <span style={{ ...styles.roleIcon, color: roleColor }}>{roleIcon}</span>
        <div style={styles.unitInfo}>
          <span style={styles.unitName}>{def.name}</span>
          <span style={styles.unitOwned}>{formatNumber(owned)} owned</span>
        </div>
      </div>
      <div style={styles.unitRight}>
        <div style={styles.unitCost}>
          <span style={{ color: canAfford ? '#4af0c4' : '#6b7a99' }}>
            {formatNumber(cost)} {primaryLabel}
          </span>
        </div>
        <button
          style={{ ...styles.buyBtn, ...(canAfford ? {} : styles.buyBtnDisabled) }}
          onClick={() => canAfford && onBuy(qty > 0 ? qty : 1)}
          disabled={!canAfford}
        >
          {selectedQty === 'max' ? (qty > 0 ? `Buy ${formatNumber(qty)}` : 'Max') : `Buy ×${qty}`}
        </button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  panel: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    background: 'rgba(10,14,26,0.98)',
  },
  qtyRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 12px',
    borderBottom: '1px solid rgba(74,240,196,0.1)',
  },
  qtyLabel: { fontSize: 11, color: '#4a6080', marginRight: 4, textTransform: 'uppercase', letterSpacing: '0.08em' },
  qtyBtn: {
    padding: '3px 10px',
    background: 'rgba(74,240,196,0.05)',
    border: '1px solid rgba(74,240,196,0.2)',
    borderRadius: 4,
    color: '#8ba0c0',
    fontSize: 12,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  qtyBtnActive: {
    background: 'rgba(74,240,196,0.15)',
    border: '1px solid rgba(74,240,196,0.6)',
    color: '#4af0c4',
    fontWeight: 700,
  },
  scroll: {
    flex: 1,
    overflowY: 'auto',
    padding: '4px 0',
  },
  chainSection: { marginBottom: 8 },
  chainHeader: {
    fontSize: 9,
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    fontWeight: 700,
    padding: '6px 12px 2px',
  },
  unitRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.03)',
    transition: 'background 0.1s',
    gap: 8,
  },
  unitLeft: { display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 },
  roleIcon: { fontSize: 14, flexShrink: 0 },
  unitInfo: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  unitName: { fontSize: 13, fontWeight: 500, color: '#c8d8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  unitOwned: { fontSize: 10, color: '#4a6080' },
  unitRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0 },
  unitCost: { fontSize: 11 },
  buyBtn: {
    padding: '4px 10px',
    background: 'rgba(74,240,196,0.15)',
    border: '1px solid rgba(74,240,196,0.4)',
    borderRadius: 4,
    color: '#4af0c4',
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  buyBtnDisabled: {
    background: 'rgba(74,96,128,0.08)',
    border: '1px solid rgba(74,96,128,0.2)',
    color: '#3a5070',
    cursor: 'not-allowed',
  },
};
